import { useState, useRef, useCallback, RefObject } from "react";

export function useScreenshot(
  webcamRef: RefObject<any>,
  canvasRef: RefObject<HTMLCanvasElement>,
  isLoading: boolean
) {
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [screenshotDims, setScreenshotDims] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });
  const imgRef = useRef<HTMLImageElement>(null);

  const handleSetScreenshot = useCallback((imageSrc: string) => {
    const img = new window.Image();
    img.onload = () => {
      setScreenshotDims({ width: img.naturalWidth, height: img.naturalHeight });
      setScreenshot(imageSrc);
    };
    img.src = imageSrc;
  }, []);

  const capturePhoto = useCallback(() => {
    if (!window.cv) {
      return;
    }
    if (!webcamRef.current || !canvasRef.current) {
      return;
    }
    if (screenshot || isLoading) {
      return;
    }
    const video = webcamRef.current.video as HTMLVideoElement;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (
      !video ||
      !video.videoWidth ||
      !video.videoHeight ||
      video.videoWidth <= 0 ||
      video.videoHeight <= 0
    ) {
      return;
    }
    if (
      video.paused ||
      video.ended ||
      video.readyState < video.HAVE_ENOUGH_DATA
    ) {
      if (video.paused) {
        video
          .play()
          .catch((e) => console.error("Error trying to play video:", e));
      }
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const QUALITY = 0.8;
      const imageSrc = canvas.toDataURL("image/jpeg", QUALITY);
      handleSetScreenshot(imageSrc);
    }
  }, [screenshot, isLoading, handleSetScreenshot, webcamRef, canvasRef]);

  return {
    screenshot,
    screenshotDims,
    setScreenshot,
    handleSetScreenshot,
    capturePhoto,
    imgRef,
  };
}
